import { useMemo, useState, type ReactNode } from 'react';
import { UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	buildPatientDetailSections,
	buildUserDetailSections,
	type DetailSection,
} from '@/utils/participantDetailDisplay';
import { buildSummaryFromSession } from '../../utils/prontuarioPatientSummary';
import { resolveProntuarioProfileHeroMeta } from '../../utils/resolveProntuarioProfileHeroMeta';
import type { ProntuarioPatientHeader } from '../../utils/resolveProntuarioPatientHeader';
import type { ProntuarioClinicalHistoryRecord } from '../../types/prontuarioClinicalHistory';
import type { ProntuarioTherapeuticPlanRecord } from '../../types/prontuarioTherapeuticPlan';
import { SessionCallProntuarioHistoricoTab } from '../SessionCallProntuarioHistoricoTab';
// import { SessionCallProntuarioPlanoTerapeuticoTab } from '../SessionCallProntuarioPlanoTerapeuticoTab';
import {
	PRONTUARIO_PANEL_SCROLL_CLASS,
	PRONTUARIO_PROFILE_SECTION_TITLE_CLASS,
} from '../../constants/prontuarioTheme';
import {
	ProntuarioProfileFieldList,
	type ProntuarioProfileField,
} from './ProntuarioProfileFieldList';
import { SessionCallProntuarioPatientProfileHero } from './SessionCallProntuarioPatientProfileHero';
import { ProntuarioProfileAccordionBlock } from './ProntuarioProfileAccordionBlock';
import { ProntuarioProfileEmptyState } from './ProntuarioProfileEmptyState';

type ProfileAccordionSection = 'data' | 'clinical-history' | 'therapeutic-plan';

const PRONTUARIO_PROFILE_EXCLUDED_SECTIONS = new Set([
	'Identificação',
	'Registro do prontuário',
]);

type ProntuarioProfileVariant = 'dark' | 'light';

const PROFILE_SECTION_TITLE_CLASS = {
	dark: PRONTUARIO_PROFILE_SECTION_TITLE_CLASS,
	light: 'px-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-600',
} as const satisfies Record<ProntuarioProfileVariant, string>;

type SessionCallProntuarioPatientDetailViewProps = {
	header: ProntuarioPatientHeader;
	session?: Sessions.Session | null;
	patient?: Patients.Patient | null;
	user?: Users.User | null;
	clinicalHistory?: ProntuarioClinicalHistoryRecord | null;
	therapeuticPlan?: ProntuarioTherapeuticPlanRecord | null;
	isLoading?: boolean;
	isError?: boolean;
	className?: string;
	variant?: ProntuarioProfileVariant;
	includeClinicalSections?: boolean;
	includeProfileHero?: boolean;
};

const ProntuarioProfileSection = ({
	title,
	children,
	className,
	variant = 'dark',
}: {
	title: string;
	children: ReactNode;
	className?: string;
	variant?: ProntuarioProfileVariant;
}) => (
	<section className={cn('min-w-0 space-y-2', className)}>
		<h2 className={PROFILE_SECTION_TITLE_CLASS[variant]}>{title}</h2>
		{children}
	</section>
);

const ProntuarioProfileFieldsSection = ({
	section,
	variant = 'dark',
}: {
	section: DetailSection;
	variant?: ProntuarioProfileVariant;
}) => {
	const fields: ProntuarioProfileField[] = section.fields
		.filter((field) => field.value?.trim())
		.map((field) => ({
			label: field.label,
			value: field.value!.trim(),
			multiline: field.fullWidth,
		}));

	if (!fields.length) return null;

	return (
		<ProntuarioProfileSection title={section.title} variant={variant}>
			<ProntuarioProfileFieldList fields={fields} variant={variant} />
		</ProntuarioProfileSection>
	);
};

const buildFallbackDetailSections = (
	session?: Sessions.Session | null,
): DetailSection[] => {
	const summary = buildSummaryFromSession(session);

	return [
		{
			title: 'Contato',
			fields: [
				{
					label: 'Telefone',
					value: summary.phone !== '—' ? summary.phone : null,
				},
				{
					label: 'Profissão',
					value:
						summary.occupation !== '—' ? summary.occupation : null,
					fullWidth: true,
				},
			],
		},
	];
};

const SessionCallProntuarioPatientDetailView = ({
	header,
	session,
	patient,
	user,
	clinicalHistory = null,
	therapeuticPlan: _therapeuticPlan = null,
	isLoading = false,
	isError = false,
	className,
	variant = 'dark',
	includeClinicalSections = true,
	includeProfileHero = true,
}: SessionCallProntuarioPatientDetailViewProps) => {
	const profileHeroMeta = useMemo(
		() => resolveProntuarioProfileHeroMeta(patient, user, session),
		[patient, session, user],
	);

	const detailSections = useMemo(() => {
		const sections = patient
			? buildPatientDetailSections(patient)
			: user
			  ? buildUserDetailSections(user)
			  : buildFallbackDetailSections(session);

		return sections.filter(
			(section) =>
				!PRONTUARIO_PROFILE_EXCLUDED_SECTIONS.has(section.title),
		);
	}, [patient, session, user]);

	const hasDetailFields = useMemo(
		() =>
			detailSections.some((section) =>
				section.fields.some((field) => field.value?.trim()),
			),
		[detailSections],
	);

	if (isLoading) {
		return (
			<p
				className={cn(
					'py-12 text-center text-sm',
					variant === 'light' ? 'text-zinc-400' : 'text-slate-500',
					className,
				)}
			>
				Carregando informações do paciente...
			</p>
		);
	}

	if (isError) {
		return (
			<p
				className={cn(
					'py-12 text-center text-sm',
					variant === 'light' ? 'text-zinc-500' : 'text-slate-500',
					className,
				)}
			>
				Não foi possível carregar as informações do participante.
			</p>
		);
	}

	const detailFieldsContent = (
		<div className="space-y-6">
			{detailSections.map((section) => (
				<ProntuarioProfileFieldsSection
					key={section.title}
					section={section}
					variant={variant}
				/>
			))}
		</div>
	);

	if (includeClinicalSections) {
		return (
			<SessionCallProntuarioPatientDetailAccordionView
				className={className}
				variant={variant}
				includeProfileHero={includeProfileHero}
				header={header}
				profileHeroMeta={profileHeroMeta}
				detailFieldsContent={detailFieldsContent}
				hasDetailFields={hasDetailFields}
				clinicalHistory={clinicalHistory}
			/>
		);
	}

	return (
		<div className={cn('space-y-6 pb-4', className)}>
			{includeProfileHero ? (
				<SessionCallProntuarioPatientProfileHero
					header={header}
					cpf={profileHeroMeta.cpf}
					gender={profileHeroMeta.gender}
					variant={variant}
				/>
			) : null}

			{detailFieldsContent}
		</div>
	);
};

type SessionCallProntuarioPatientDetailAccordionViewProps = {
	className?: string;
	variant: ProntuarioProfileVariant;
	includeProfileHero: boolean;
	header: ProntuarioPatientHeader;
	profileHeroMeta: ReturnType<typeof resolveProntuarioProfileHeroMeta>;
	detailFieldsContent: ReactNode;
	hasDetailFields: boolean;
	clinicalHistory: ProntuarioClinicalHistoryRecord | null;
};

const SessionCallProntuarioPatientDetailAccordionView = ({
	className,
	variant,
	includeProfileHero,
	header,
	profileHeroMeta,
	detailFieldsContent,
	hasDetailFields,
	clinicalHistory,
}: SessionCallProntuarioPatientDetailAccordionViewProps) => {
	const [activeSection, setActiveSection] =
		useState<ProfileAccordionSection | null>(null);

	const handleSelectSection = (section: ProfileAccordionSection) => {
		setActiveSection((current) => (current === section ? null : section));
	};

	return (
		<div
			className={cn(
				'flex min-h-0 flex-1 flex-col overflow-hidden',
				className,
			)}
		>
			{includeProfileHero ? (
				<div className="shrink-0 border-b border-white/[0.08] px-5 py-5">
					<SessionCallProntuarioPatientProfileHero
						header={header}
						cpf={profileHeroMeta.cpf}
						gender={profileHeroMeta.gender}
						variant={variant}
					/>
				</div>
			) : null}

			<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
				<ProntuarioProfileAccordionBlock
					label="Dados"
					variant={variant}
					isActive={activeSection === 'data'}
					onClick={() => handleSelectSection('data')}
					fillAvailable
					contentScrollClass={PRONTUARIO_PANEL_SCROLL_CLASS}
				>
					{hasDetailFields ? (
						detailFieldsContent
					) : (
						<ProntuarioProfileEmptyState
							variant={variant}
							title="Nenhum dado registrado"
							description="As informações do participante aparecerão aqui quando estiverem disponíveis."
							icon={
								<UserRound
									className="size-5 shrink-0"
									strokeWidth={2}
								/>
							}
						/>
					)}
				</ProntuarioProfileAccordionBlock>

				<ProntuarioProfileAccordionBlock
					label="Histórico clínico"
					variant={variant}
					isActive={activeSection === 'clinical-history'}
					onClick={() => handleSelectSection('clinical-history')}
					fillAvailable
					contentScrollClass={PRONTUARIO_PANEL_SCROLL_CLASS}
				>
					<SessionCallProntuarioHistoricoTab
						variant="profile"
						profileTheme={variant}
						record={clinicalHistory}
					/>
				</ProntuarioProfileAccordionBlock>

				{/* <ProntuarioProfileAccordionBlock
					label="Plano terapêutico"
					variant={variant}
					isActive={activeSection === 'therapeutic-plan'}
					onClick={() => handleSelectSection('therapeutic-plan')}
					fillAvailable
					contentScrollClass={PRONTUARIO_PANEL_SCROLL_CLASS}
				>
					<SessionCallProntuarioPlanoTerapeuticoTab
						variant="profile"
						profileTheme={variant}
						plan={therapeuticPlan}
					/>
				</ProntuarioProfileAccordionBlock> */}
			</div>
		</div>
	);
};

export { SessionCallProntuarioPatientDetailView };
